import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { deleteInvoice, getClientById } from '../../firebase/database';
import { generateRevenuesInvoicePDF, openPDFInNewTab } from '../../services/invoicePdfService';
import { LogisticsInvoice } from '../../types';
import toast, { Toaster } from 'react-hot-toast';

const AllInvoices: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentMode } = useAuth();
  const [invoices, setInvoices] = useState<LogisticsInvoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<LogisticsInvoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'draft'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(null);
  const [generatingPDFId, setGeneratingPDFId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // Default to newest first

  // Set search term from navigation state
  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
    }
  }, [location.state]);

  useEffect(() => {
    const load = async () => {
      
      try {
        setLoading(true);
        
        // Get ALL invoices from the database
        const { getInvoices } = await import('../../firebase/database');
        const allInvoices = await getInvoices();
        
        setInvoices(allInvoices);
        setFilteredInvoices(allInvoices);
      } catch (e) {
        console.error('AllRevenueInvoices: Error loading invoices:', e);
        toast.error('Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // Remove dependency on currentUser since we're loading all invoices

  useEffect(() => {
    let filtered = invoices;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => 
        invoice.invoiceNumber.toLowerCase().includes(term) ||
        invoice.clientName.toLowerCase().includes(term) ||
        invoice.invoiceType.toLowerCase().includes(term)
      );
    }

    // Apply date sorting
    filtered = [...filtered].sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
      const dateB = b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    setFilteredInvoices(filtered);
  }, [invoices, statusFilter, searchTerm, sortOrder]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-orange-100 text-orange-800';
      case 'deleted':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'draft':
        return 'Draft';
      case 'deleted':
        return 'Deleted';
      default:
        return status;
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingInvoiceId(invoiceId);
      await deleteInvoice(invoiceId);
      
      // Remove the invoice from the local state
      setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      toast.success('Invoice deleted successfully');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  const handleViewPDF = async (invoice: LogisticsInvoice) => {
    try {
      setGeneratingPDFId(invoice.id);
      // Get client address
      const client = await getClientById(invoice.clientId);
      const clientAddress = client?.address;
      
      const pdf = await generateRevenuesInvoicePDF(invoice, clientAddress);
      openPDFInNewTab(pdf, 'Invoice PDF');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    } finally {
      setGeneratingPDFId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">All Revenue Invoices</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                                 <option value="all">All Statuses</option>
                 <option value="pending">Pending</option>
                 <option value="approved">Approved</option>
                 <option value="rejected">Rejected</option>
                 <option value="draft">Draft</option>
              </select>
            </div>
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                id="search"
                placeholder="Search by invoice number, client name, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {filteredInvoices.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoices Found</h3>
            <p className="text-gray-600">
              {invoices.length === 0 
                ? "You have no invoices yet." 
                : "No invoices match your current filters."}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                        onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                        title="Click to sort by date"
                      >
                        <div className="flex items-center space-x-1">
                          <span>Date</span>
                          <span className="text-gray-400">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inv.invoiceNumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.clientName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{inv.invoiceType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.date.toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.currency} {inv.total.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(inv.status)}`}>
                            {getStatusText(inv.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-2">
                            {(currentMode === 'authorizer' || (currentMode === 'inputter' && (inv.status === 'draft' || inv.status === 'rejected'))) && (
                              <>
                                <button
                                  className="text-indigo-600 hover:text-indigo-900"
                                  onClick={() => navigate(`/edit-invoice/${inv.id}`)}
                                >
                                  Edit
                                </button>
                                <span className="text-gray-400">|</span>
                              </>
                            )}
                            <button
                              className="text-green-600 hover:text-green-900"
                              onClick={() => handleViewPDF(inv)}
                              disabled={generatingPDFId === inv.id}
                            >
                              {generatingPDFId === inv.id ? 'Generating...' : 'View PDF'}
                            </button>
                            {(currentMode === 'authorizer' || (currentMode === 'inputter' && (inv.status === 'draft' || inv.status === 'rejected'))) && (
                              <>
                                <span className="text-gray-400">|</span>
                                <button
                                  className="text-red-600 hover:text-red-900"
                                  onClick={() => handleDeleteInvoice(inv.id)}
                                  disabled={deletingInvoiceId === inv.id}
                                >
                                  {deletingInvoiceId === inv.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllInvoices;
